/**
 * Buy Score weight components flat region per scope §6.4. Atlas Day 2 Phase
 * 4 + Atlas Day 3a P1 cross-cell pointer wiring.
 *
 * Surface (D1 (α) lock 2026-05-27 — catalog only): "Weight components —
 * ISG defines N candidate metrics for ranking; K active. Per-session
 * weighting in buying sessions; tooltips surface in BuyingSession headers.
 * → composed into Buy Score above"
 *
 * D1 (α) lock applied: weights live in buying_sessions.ui_state.weights
 * (per-session scenario state), NOT engine substrate. Atlas surfaces bswc
 * catalog (engine state) only; sum-to-100 phrasing dropped from scope §6.4.
 *
 * F33 trap warning per scope §6.4 + §6.6.5 + Phase 0 #11: inline warning
 * when any bswc row has is_available=true AND applies_to_metric IS NULL
 * (engine silently zeros the contribution per Day 91a F33 framing).
 * Phase 0 #11 verified F33 trap count = 0 today; warning suppresses at
 * 0 count, surfaces inline at count > 0.
 *
 * Cross-cell pointer: "→ composed into Buy Score above" wired per Atlas
 * Day 3a P1 — clickable button with stopPropagation; navigates to
 * buy-score-composition inspect. Region body navigates to weight-components
 * inspect. Refactored from <button> to <div role="button"> per Day 3a
 * cross-cell pointer pattern.
 */

import type { JSX, KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Scale, AlertTriangle } from 'lucide-react';
import type { WeightComponentsSubstrate } from '../../../lib/atlas/substrateCalcSubstrate';

export interface WeightComponentsRegionProps {
  substrate: WeightComponentsSubstrate;
}

export default function WeightComponentsRegion({
  substrate,
}: WeightComponentsRegionProps): JSX.Element {
  const navigate = useNavigate();
  const { total, active, f33TrapRows } = substrate;
  const goPrimary = (): void => {
    navigate('/admin/engine-atlas/inspect/weight-components');
  };
  const onKey = (e: KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      goPrimary();
    }
  };
  return (
    <div
      role="button"
      tabIndex={0}
      data-sub-region-id="weight-components"
      onClick={goPrimary}
      onKeyDown={onKey}
      className="w-full text-left border border-border-subtle bg-surface-0 rounded-md p-3 hover:border-accent transition-colors cursor-pointer"
    >
      <div className="flex items-start gap-2">
        <Scale size={16} className="text-ink-secondary flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-2">
            <h3 className="text-sm font-semibold text-ink-bright">Weight components</h3>
            <button
              type="button"
              data-cross-cell-pointer="buy-score-composition"
              onClick={(e) => {
                e.stopPropagation();
                navigate('/admin/engine-atlas/inspect/buy-score-composition');
              }}
              className="text-xs text-ink-muted hover:text-accent transition-colors cursor-pointer"
            >
              → composed into Buy Score above
            </button>
          </div>
          <p className="text-xs text-ink-secondary mt-1">
            ISG defines {total} candidate {total === 1 ? 'metric' : 'metrics'} for ranking; {active}{' '}
            active. Per-session weighting in buying sessions; tooltips surface in BuyingSession
            headers.
          </p>

          {/* F33 trap inline warning per scope §6.4 + §6.6.5 — conditional on substrate state */}
          {f33TrapRows > 0 && (
            <div className="mt-2 flex items-start gap-1.5 text-xs text-amber-500">
              <AlertTriangle size={12} className="flex-shrink-0 mt-0.5" />
              <span>
                Configuration trap: {f33TrapRows}{' '}
                {f33TrapRows === 1 ? 'weight component' : 'weight components'} active but not mapped
                to a metric. Engine silently zeros the contribution.
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
